import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem as MuiMenuItem, Paper } from '@mui/material';
import { OperationDashboard } from '@pages/dashboard';
import { GlobalContext } from 'context/userContext';
import { login } from '@service/current-user';
import { AxiosInstance } from '@service/axios-instance';

export function TvDashboard() {
  const { setOperation } = useContext(GlobalContext);

  const [isConfigured, setIsConfigured] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [dbList, setDbList] = useState<string[]>([]);
  const [selectedDb, setSelectedDb] = useState('');
  const [step, setStep] = useState(1); // 1 = Login, 2 = Select DB
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto load from local storage
  useEffect(() => {
    const savedUser = localStorage.getItem('tv_username');
    const savedPass = localStorage.getItem('tv_password');
    const savedDb = localStorage.getItem('tv_operation');

    if (savedUser && savedPass && savedDb) {
      // Background login to refresh cookie
      login({ username: savedUser, password: savedPass })
        .then(() => {
          setOperation(savedDb);
          localStorage.setItem("selectedDB", savedDb);
          setIsConfigured(true);
        })
        .catch((err) => {
          console.error("TV Auto-Login failed:", err);
          setError("Sessão da TV expirada ou senha alterada. Faça login novamente.");
          localStorage.removeItem('tv_username');
          localStorage.removeItem('tv_password');
          localStorage.removeItem('tv_operation');
        });
    }
  }, [setOperation]);

  // Keep session alive every 10 minutes if configured
  useEffect(() => {
    if (!isConfigured) return;

    const interval = setInterval(() => {
      const savedUser = localStorage.getItem('tv_username');
      const savedPass = localStorage.getItem('tv_password');
      if (savedUser && savedPass) {
        login({ username: savedUser, password: savedPass }).catch(err => console.error("Silent relogin failed", err));
      }
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(interval);
  }, [isConfigured]);

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      await login({ username: username.trim(), password });
    } catch (err) {
      console.error(err);
      setError('Credenciais inválidas');
      setLoading(false);
      return;
    }

    try {
      // Fetch available DBs for this user
      const response = await AxiosInstance.get('/general/get_dbs', { withCredentials: true });
      const dbs = response.data || [];
      if (dbs.length === 0) {
        setError('Usuário não tem acesso a nenhuma operação.');
      } else {
        setDbList(dbs);
        setStep(2);
      }
    } catch (err) {
      console.error(err);
      setError('Erro ao buscar operações.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTv = () => {
    localStorage.setItem('tv_username', username);
    localStorage.setItem('tv_password', password);
    localStorage.setItem('tv_operation', selectedDb);

    setOperation(selectedDb);
    localStorage.setItem("selectedDB", selectedDb);
    setIsConfigured(true);
  };

  const handleClearTv = () => {
    localStorage.removeItem('tv_username');
    localStorage.removeItem('tv_password');
    localStorage.removeItem('tv_operation');
    setIsConfigured(false);
    setStep(1);
    setUsername('');
    setPassword('');
  };

  if (isConfigured) {
    return (
      <Box sx={{ width: '100vw', height: '100vh', overflow: 'auto', bgcolor: 'background.default', position: 'relative' }}>
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={handleClearTv}
          sx={{ position: 'absolute', top: 10, right: 10, zIndex: 9999, opacity: 0.2, '&:hover': { opacity: 1 } }}
        >
          Sair do Modo TV
        </Button>
        <OperationDashboard hideUI={true} />
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh" bgcolor="#f5f5f5">
      <Paper elevation={4} sx={{ p: 4, maxWidth: 400, width: '100%', textAlign: 'center' }}>
        <Typography variant="h5" mb={3} fontWeight="bold" color="primary">Telemetria - TV</Typography>

        {error && <Typography color="error" mb={2} variant="body2">{error}</Typography>}

        {step === 1 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Usuário"
              variant="outlined"
              value={username}
              onChange={e => setUsername(e.target.value)}
              fullWidth
            />
            <TextField
              label="Senha"
              variant="outlined"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleLogin}
              disabled={!username || !password || loading}
            >
              {loading ? 'Autenticando...' : 'Autenticar'}
            </Button>
          </Box>
        )}

        {step === 2 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2">Selecione a operação para exibir no mapa:</Typography>
            <FormControl fullWidth>
              <InputLabel>Operação</InputLabel>
              <Select
                value={selectedDb}
                label="Operação"
                onChange={e => setSelectedDb(e.target.value)}
              >
                {dbList.map(db => (
                  <MuiMenuItem key={db} value={db}>{db}</MuiMenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="success"
              onClick={handleStartTv}
              disabled={!selectedDb}
            >
              Iniciar Modo TV
            </Button>
            <Button variant="text" onClick={() => setStep(1)}>
              Voltar
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
